/**
 * BLRRD Batch — orchestrate multi-file format conversion.
 *
 * Phase 3.A.batch.v1: format conversion only (no preset). Built-in
 * presets use post-effects (clarity/blur/sharpen/vignette/grain)
 * that live in legacy editor's renderPreview, not in the ESM kernel
 * pass. Until those effects are extracted into an ESM module, batch
 * preset application would silently diverge from single-file output
 * — Codex r1 STOP-2 alternative: explicitly exclude. v1.5 follow-up.
 *
 * Public API (mounted on window.BlrrdBatch):
 *
 *   BlrrdBatch.start(files, opts) → BatchHandle
 *     where BatchHandle = {
 *       cancel: () => void,
 *       done:   Promise<BatchResult>,
 *       events: AsyncIterable<{type, fileIndex, total, blob, error, status}>,
 *     }
 *     and BatchResult = {
 *       outputs:  Array<{ name, blob, error?: string }>,
 *       cancelled: boolean,
 *     }
 *
 *   BlrrdBatch.runZip(files, opts) → Promise<{ blob, cancelled }>
 *     Convenience wrapper. Internally uses start() + JSZip.
 *
 *   BlrrdBatch.MAX_INPUT_PIXELS = 32 * 1024 * 1024  (32 MP per file)
 *   BlrrdBatch.MAX_BATCH_OUTPUT_BYTES = 2 * 1024 * 1024 * 1024  (2 GiB ZIP cap)
 *   BlrrdBatch.SAFETY_DIM_CAP = 8000  (8K longest edge before kernel)
 *
 * @module src/pipeline/batch
 */

import {
    ingestSRGB, ingestSRGBFloat, ingestLinearACEScg, ingestLinearRec709,
    ingestWithMatrix,
} from './ingest.js';

export const MAX_INPUT_PIXELS       = 32 * 1024 * 1024;
export const MAX_BATCH_OUTPUT_BYTES = 2 * 1024 * 1024 * 1024;
export const SAFETY_DIM_CAP         = 8000;

const OUTPUT_DIM_PRESETS = {
    'original': null,
    '4k':       3840,
    'fhd':      1920,
    'hd':       1280,
};

let jszipPromise = null;
function loadJSZip() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.reject(new Error('JSZip needs a browser environment'));
    }
    if (window.JSZip) return Promise.resolve(window.JSZip);
    if (jszipPromise) return jszipPromise;
    jszipPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = '/vendor/jszip/jszip.min.js';
        s.async = true;
        s.onload = () => {
            if (window.JSZip) resolve(window.JSZip);
            else reject(new Error('JSZip loaded but window.JSZip not defined'));
        };
        s.onerror = () => reject(new Error('Failed to load /vendor/jszip/jszip.min.js'));
        document.head.appendChild(s);
    });
    return jszipPromise;
}

/* Decode a blob to a canonical batch source: { kind, rgba, w, h, colorSpace }.
 * Hides the native-vs-decoded split from the orchestrator. (Codex r2 STOP-1.) */
async function decodeToBatchSource(blob) {
    if (!window.BlrrdDecode || typeof window.BlrrdDecode.decode !== 'function') {
        throw new Error('BlrrdDecode not loaded');
    }
    const result = await window.BlrrdDecode.decode(blob);
    if (result.kind === 'native') {
        /* Native PNG/JPEG/WebP/AVIF/GIF/BMP — readback via createImageBitmap
         * and a canvas. Chrome and Firefox have createImageBitmap; Safari's
         * support is solid since 14. */
        const bitmap = await createImageBitmap(blob);
        const w = bitmap.width, h = bitmap.height;
        const cnv = typeof OffscreenCanvas !== 'undefined'
            ? new OffscreenCanvas(w, h)
            : Object.assign(document.createElement('canvas'), { width: w, height: h });
        const ctx = cnv.getContext('2d');
        ctx.drawImage(bitmap, 0, 0);
        const imgData = ctx.getImageData(0, 0, w, h);
        try { bitmap.close && bitmap.close(); } catch { /* ignore */ }
        return {
            kind: 'rgba8',
            rgba: new Uint8ClampedArray(imgData.data),
            w, h,
            colorSpace: 'srgb',
        };
    }
    /* Decoded results pass through unchanged. */
    return result;
}

/* Apply the right ingest function based on the decoded result's
 * colour-space tag. (Codex r2 STOP-4 — explicit ingest routing.) */
function ingestSource(src) {
    const { kind, rgba, w, h, colorSpace, dcpMatrix } = src;
    if (kind === 'rgba8') {
        return ingestSRGB(rgba, w, h);
    }
    if (kind === 'rgbaFloat') {
        /* Phase 3.B-color P4 — RAW decode produces a `dcpMatrix` field
         * when the camera has a registered DCP profile. Apply the
         * cameraRGB→Rec709 transform via ingestWithMatrix; otherwise
         * fall back to the generic linear-Rec709 ingest. */
        if (colorSpace === 'linear-rec709' && dcpMatrix && dcpMatrix.length === 9) {
            return ingestWithMatrix(rgba, w, h, dcpMatrix);
        }
        if (colorSpace === 'srgb') return ingestSRGBFloat(rgba, w, h);
        if (colorSpace === 'linear-rec709') return ingestLinearRec709(rgba, w, h);
        if (colorSpace === 'aces-ap1' || colorSpace === 'aces-ap0') {
            return ingestLinearACEScg(rgba, w, h);
        }
    }
    throw new Error(`Unknown batch-source shape: kind=${kind} colorSpace=${colorSpace}`);
}

/* Downsample rgba8 source to fit `maxDim` longest edge. Applied
 * BEFORE kernel to enforce the 8K safety cap. Returns the same
 * shape but with smaller w/h. (Codex r2 STOP-3 + STOP-5.) */
function safetyDownsample(src, maxDim) {
    const { w, h } = src;
    const longest = Math.max(w, h);
    if (longest <= maxDim) return src;
    const scale = maxDim / longest;
    const nw = Math.max(1, Math.round(w * scale));
    const nh = Math.max(1, Math.round(h * scale));
    /* Use canvas for a single-pass smooth downsample. Acceptable for
     * the safety cap path since user opted into batch (precision is
     * not the main concern when input is > 8K). */
    if (src.kind === 'rgba8') {
        const cnv = typeof OffscreenCanvas !== 'undefined'
            ? new OffscreenCanvas(w, h)
            : Object.assign(document.createElement('canvas'), { width: w, height: h });
        cnv.getContext('2d').putImageData(new ImageData(src.rgba, w, h), 0, 0);
        const target = typeof OffscreenCanvas !== 'undefined'
            ? new OffscreenCanvas(nw, nh)
            : Object.assign(document.createElement('canvas'), { width: nw, height: nh });
        const ctx = target.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(cnv, 0, 0, nw, nh);
        const out = ctx.getImageData(0, 0, nw, nh);
        return {
            kind: 'rgba8',
            rgba: new Uint8ClampedArray(out.data),
            w: nw, h: nh,
            colorSpace: src.colorSpace,
        };
    }
    /* For rgbaFloat we can't easily route through canvas without a
     * lossy quantize round-trip. Refuse > safety cap on float inputs;
     * the user can pre-scale their TIFF/EXR if they really want to
     * batch-process huge files. Realistic photo workflows stay <8K. */
    throw new Error(`Float input ${w}×${h} exceeds safety cap ${maxDim}; use Original or smaller dim`);
}

/* Compute output (post-kernel) dims from user choice + working dims. */
function computeOutputDims(workingW, workingH, outputDimChoice) {
    const cap = OUTPUT_DIM_PRESETS[outputDimChoice] || OUTPUT_DIM_PRESETS['original'];
    if (!cap) return { w: workingW, h: workingH };
    const longest = Math.max(workingW, workingH);
    if (longest <= cap) return { w: workingW, h: workingH };
    const scale = cap / longest;
    return {
        w: Math.max(1, Math.round(workingW * scale)),
        h: Math.max(1, Math.round(workingH * scale)),
    };
}

/* Build a NEUTRAL pipeline context — used by every batch run because
 * preset application is deferred to v1.5. */
function buildNeutralContext() {
    if (!window.BlrrdPipeline || typeof window.BlrrdPipeline.buildPipelineContext !== 'function') {
        throw new Error('BlrrdPipeline.buildPipelineContext not available');
    }
    const ADJUST_NEUTRAL = {
        brightness: 0, contrast: 0, clarity: 0, highlights: 0, shadows: 0,
        whites: 0, blacks: 0, temperature: 0, tint: 0, saturation: 0, vibrance: 0,
        sharpen: 0, blur: 0, vignette: 0, grain: 0, dehaze: 0,
        hslRedH: 0, hslRedS: 0, hslRedL: 0,
        hslOrangeH: 0, hslOrangeS: 0, hslOrangeL: 0,
        hslYellowH: 0, hslYellowS: 0, hslYellowL: 0,
        hslGreenH: 0, hslGreenS: 0, hslGreenL: 0,
        hslCyanH: 0, hslCyanS: 0, hslCyanL: 0,
        hslBlueH: 0, hslBlueS: 0, hslBlueL: 0,
        hslMagentaH: 0, hslMagentaS: 0, hslMagentaL: 0,
    };
    const GRADE_NEUTRAL = {
        liftR: 0, liftG: 0, liftB: 0, liftY: 0,
        gammaR: 0, gammaG: 0, gammaB: 0, gammaY: 0,
        gainR: 0, gainG: 0, gainB: 0, gainY: 0,
        offsetR: 0, offsetG: 0, offsetB: 0, offsetMaster: 0,
        gradeContrast: 1, gradePivot: 0.18,
        gradeColorBoost: 0, gradeShadows: 0, gradeHighlights: 0,
        gradeSaturation: 50, gradeHue: 50, gradeLumMix: 100,
    };
    const CURVES_NEUTRAL = {
        master: [{ x: 0, y: 0 }, { x: 1, y: 1 }],
        r:      [{ x: 0, y: 0 }, { x: 1, y: 1 }],
        g:      [{ x: 0, y: 0 }, { x: 1, y: 1 }],
        b:      [{ x: 0, y: 0 }, { x: 1, y: 1 }],
    };
    const INTENSITY_NEUTRAL = { master: 100, r: 100, g: 100, b: 100 };
    /* Identity curve LUT — same shape the main editor uses. */
    const helpers = {
        buildCurveLUTF: () => {
            const N = 1024;
            const lut = new Float32Array(N);
            for (let i = 0; i < N; i++) lut[i] = i / (N - 1);
            return lut;
        },
    };
    return window.BlrrdPipeline.buildPipelineContext(
        ADJUST_NEUTRAL, GRADE_NEUTRAL, CURVES_NEUTRAL, INTENSITY_NEUTRAL, helpers,
    );
}

/* Generate output filename from input filename. Replace extension. */
function outputName(inputName, outputFormat, index) {
    const base = (inputName || `image-${index + 1}`).replace(/\.[^.]+$/, '') || `image-${index + 1}`;
    const ext = outputFormat === 'jpeg' ? 'jpg' : outputFormat;
    return `${base}.${ext}`;
}

/* Disambiguate filename collisions in a batch — append -2, -3 etc. */
function dedupeNames(names) {
    const seen = Object.create(null);
    return names.map((name) => {
        if (!seen[name]) { seen[name] = 1; return name; }
        const dot = name.lastIndexOf('.');
        const stem = dot > 0 ? name.slice(0, dot) : name;
        const ext  = dot > 0 ? name.slice(dot)    : '';
        let n = ++seen[name];
        let candidate = `${stem}-${n}${ext}`;
        while (seen[candidate]) { n++; candidate = `${stem}-${n}${ext}`; }
        seen[candidate] = 1;
        return candidate;
    });
}

/* The public BatchHandle class. */
class BatchHandle {
    constructor(files, opts) {
        this._files = Array.from(files || []);
        this._opts = {
            outputFormat:    'jpeg',
            outputQuality:   0.92,
            outputDim:       'original',
            viewTone:        'neutral',
            outputSpace:     'sRGB',
            onProgress:      null,
            ...opts,
        };
        this._cancelled = false;
        this._abortCtrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
        this._worker = null;
        this._eventQueue = [];
        this._eventResolvers = [];
        this.outputs = [];
        this.done = this._run();
    }

    cancel() {
        if (this._cancelled) return;
        this._cancelled = true;
        if (this._abortCtrl) {
            try { this._abortCtrl.abort(); } catch { /* ignore */ }
        }
        if (this._worker) {
            try { this._worker.terminate(); } catch { /* ignore */ }
            this._worker = null;
        }
        this._emit({ type: 'cancelled' });
        this._closeEventStream();
    }

    /* Async iterator over progress events. */
    [Symbol.asyncIterator]() {
        return {
            next: () => {
                if (this._eventQueue.length > 0) {
                    return Promise.resolve({ value: this._eventQueue.shift(), done: false });
                }
                if (this._eventClosed) return Promise.resolve({ value: undefined, done: true });
                return new Promise((resolve) => this._eventResolvers.push(resolve));
            },
        };
    }

    _emit(event) {
        if (this._eventResolvers.length > 0) {
            const resolve = this._eventResolvers.shift();
            resolve({ value: event, done: false });
        } else {
            this._eventQueue.push(event);
        }
    }

    _closeEventStream() {
        this._eventClosed = true;
        while (this._eventResolvers.length > 0) {
            const resolve = this._eventResolvers.shift();
            resolve({ value: undefined, done: true });
        }
    }

    async _run() {
        const total = this._files.length;
        if (total === 0) {
            this._closeEventStream();
            return { outputs: [], cancelled: this._cancelled };
        }

        try {
            /* Spawn worker via Vite-safe URL (Codex r2 STOP-2). */
            this._worker = new Worker(
                new URL('../workers/batch.worker.js', import.meta.url),
                { type: 'module' },
            );

            /* Build a single neutral context — reused across files. */
            const pipeCtx = buildNeutralContext();

            for (let i = 0; i < total; i++) {
                if (this._cancelled) break;
                const file = this._files[i];
                this._emit({ type: 'file-start', fileIndex: i, total, name: file.name });

                let entry;
                try {
                    entry = await this._processOne(file, i, pipeCtx);
                } catch (err) {
                    entry = { name: outputName(file.name, this._opts.outputFormat, i), error: err.message || String(err) };
                }
                if (this._cancelled) break;
                this.outputs.push(entry);
                this._emit({
                    type:  entry.error ? 'file-error' : 'file-done',
                    fileIndex: i,
                    total,
                    name:  entry.name,
                    error: entry.error,
                });
                if (typeof this._opts.onProgress === 'function') {
                    try { this._opts.onProgress(i + 1, total, entry); } catch { /* ignore */ }
                }
            }
        } finally {
            if (this._worker) {
                try { this._worker.terminate(); } catch { /* ignore */ }
                this._worker = null;
            }
            this._closeEventStream();
        }

        /* Disambiguate filenames in the final outputs (collisions
         * common when batches mix sRGB JPGs with same stem). */
        const names = dedupeNames(this.outputs.map((o) => o.name));
        this.outputs.forEach((o, i) => { o.name = names[i]; });

        return { outputs: this.outputs, cancelled: this._cancelled };
    }

    async _processOne(file, index, pipeCtx) {
        if (this._cancelled) throw new Error('cancelled');
        let src = await decodeToBatchSource(file);
        if (this._cancelled) throw new Error('cancelled');

        /* Hard input cap. */
        if (src.w * src.h > MAX_INPUT_PIXELS) {
            throw new Error(`Input ${src.w}×${src.h} > ${MAX_INPUT_PIXELS} pixels (max 32 MP for batch v1)`);
        }

        /* Safety cap (8K longest edge) — only triggers on inputs >8K. */
        src = safetyDownsample(src, SAFETY_DIM_CAP);
        if (this._cancelled) throw new Error('cancelled');

        /* Ingest to ACEScg Float32 on main thread (Codex r2 STOP-4). */
        const srcFloat = ingestSource(src);
        const srcU8 = src.kind === 'rgba8' ? src.rgba
            : (() => {
                /* Float source: synthesize Uint8 alpha + clipped RGB
                 * for the kernel's alpha pass-through.
                 * (kernel reads alpha from srcU8 byte 3 only.) */
                const u8 = new Uint8ClampedArray(src.rgba.length);
                for (let j = 0; j < u8.length; j += 4) {
                    u8[j + 3] = 255;
                }
                return u8;
            })();
        const w = src.w, h = src.h;
        if (this._cancelled) throw new Error('cancelled');

        /* Output dimensions. */
        const outDims = computeOutputDims(w, h, this._opts.outputDim);

        /* Post to worker, await result. AbortSignal wakes the awaiter
         * if cancel() terminates the worker mid-flight (otherwise the
         * onMessage listener never fires and the Promise hangs forever
         * — Codex r2 STOP-1 cancel-contract: done MUST resolve). */
        const signal = this._abortCtrl ? this._abortCtrl.signal : null;
        const blob = await new Promise((resolve, reject) => {
            const onMessage = (e) => {
                if (this._cancelled) return; /* Late message guard. */
                if (e.data.fileIndex !== index) return;
                this._worker.removeEventListener('message', onMessage);
                if (signal) signal.removeEventListener('abort', onAbort);
                if (e.data.error) reject(new Error(e.data.error));
                else resolve(e.data.blob);
            };
            const onAbort = () => {
                if (this._worker) {
                    try { this._worker.removeEventListener('message', onMessage); } catch { /* ignore */ }
                }
                reject(new Error('cancelled'));
            };
            if (signal) {
                if (signal.aborted) { onAbort(); return; }
                signal.addEventListener('abort', onAbort, { once: true });
            }
            this._worker.addEventListener('message', onMessage);
            /* Transfer srcFloat + srcU8 buffers (Codex r3 STOP-1):
             * structured-clone of 32MP × Float32×4 + Uint8×4 is ~640 MB
             * per file and risks main-thread OOM before ZIP memory ever
             * lands. Main has no further use for these buffers — the
             * next file allocates fresh ones via decodeToBatchSource. */
            this._worker.postMessage({
                fileIndex: index,
                srcFloat, srcU8,
                w, h,
                pipeCtx,
                outputFormat: this._opts.outputFormat,
                outputQuality: this._opts.outputQuality,
                outputW: outDims.w,
                outputH: outDims.h,
                viewTone: this._opts.viewTone,
                outputSpace: this._opts.outputSpace,
            }, [srcFloat.buffer, srcU8.buffer]);
        });
        if (this._cancelled) throw new Error('cancelled');

        return {
            name: outputName(file.name, this._opts.outputFormat, index),
            blob,
        };
    }
}

export function start(files, opts) {
    return new BatchHandle(files, opts);
}

export async function runZip(files, opts = {}) {
    const handle = start(files, opts);
    /* Don't await events — just await done. */
    const result = await handle.done;

    /* Cap total output size to keep ZIP construction in-memory. */
    let total = 0;
    for (const o of result.outputs) {
        if (o.blob) total += o.blob.size;
    }
    if (total > MAX_BATCH_OUTPUT_BYTES) {
        throw new Error(`Batch output ${(total / 1e9).toFixed(2)} GB exceeds ${(MAX_BATCH_OUTPUT_BYTES / 1e9).toFixed(2)} GB cap. Use 'Save to folder' (Chrome) for very large batches.`);
    }

    const JSZip = await loadJSZip();
    const zip = new JSZip();
    for (const o of result.outputs) {
        if (o.blob) {
            /* compression:STORE — image blobs are already compressed. */
            zip.file(o.name, o.blob, { compression: 'STORE' });
        }
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    /* Force MIME to application/zip — JSZip emits 'application/zip' by default. */
    return { blob, cancelled: result.cancelled, outputs: result.outputs };
}
